import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUI } from '@/hooks/ui/useUI';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type { PaginatedFetchParams, PaginatedFetchResult } from '@/hooks/ui/usePaginatedList';
import { useMonthlyCutsLoader } from '@/hooks/attendance';
import { paymentService } from '@/api/domains/payment';
import type { ConceptoPagoItem } from '@/api/domains/payment';
import type { TableColumn } from '@/components/ui/tables/Table';


export interface ConceptosPagoFilters {
  monthlyCutId: number;
}

export interface MonthlyCutOption {
  value: string;
  label: string;
  supportingText: string;
}

export interface UseConceptosPagoListResult {
  year: number | undefined;
  month: number | undefined;
  monthlyCutId: number | undefined;
  setYear: (year: number | undefined) => void;
  setMonth: (month: number | undefined) => void;
  setMonthlyCutId: (monthlyCutId: number | undefined) => void;
  yearOptions: Array<{ value: string; label: string }>;
  monthOptions: Array<{ value: string; label: string }>;
  monthlyCutOptions: MonthlyCutOption[];
  loadingMonthlyCuts: boolean;
  handleSearch: () => void;
  hasSearched: boolean;
  data: ConceptoPagoItem[];
  totalRecords: number;
  isLoading: boolean;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  onSearch: (text: string) => void;
  onSortChange: (columnKey: string, direction: 'asc' | 'desc') => void;
  columns: TableColumn<ConceptoPagoItem>[];
}

export const useConceptosPagoList = (): UseConceptosPagoListResult => {
  const { t: translate } = useTranslation('payment');
  const { showApiError } = useUI();

  const {
    loadingMonthlyCuts,
    monthlyCutsWithinRange,
    yearOptions,
    monthOptions,
  } = useMonthlyCutsLoader();

  const [year, setYearState] = useState<number | undefined>(undefined);
  const [month, setMonthState] = useState<number | undefined>(undefined);
  const [monthlyCutId, setMonthlyCutIdState] = useState<number | undefined>(undefined);
  const [hasSearched, setHasSearched] = useState(false);

  const setYear = useCallback((newYear: number | undefined) => {
    setYearState(newYear);
    setMonthState(undefined);
    setMonthlyCutIdState(undefined);
    setHasSearched(false);
  }, []);

  const setMonth = useCallback((newMonth: number | undefined) => {
    setMonthState(newMonth);
    setMonthlyCutIdState(undefined);
    setHasSearched(false);
  }, []);

  const setMonthlyCutId = useCallback((newMonthlyCutId: number | undefined) => {
    setMonthlyCutIdState(newMonthlyCutId);
    setHasSearched(false);
  }, []);

  const currentMonthOptions = useMemo(
    () => (year ? monthOptions(year) : []),
    [monthOptions, year],
  );

  const monthlyCutOptions: MonthlyCutOption[] = useMemo(() => (
    monthlyCutsWithinRange
      .filter((cut) => {
        if (year && cut.year !== year) return false;
        if (month && cut.month !== month) return false;
        return true;
      })
      .map((cut) => ({
        value: cut.monthlyCutId.toString(),
        label: cut.label,
        supportingText: `${cut.startDate} - ${cut.endDate}`,
      }))
  ), [monthlyCutsWithinRange, year, month]);

  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<ConceptosPagoFilters>,
      _signal: AbortSignal,
    ): Promise<PaginatedFetchResult<ConceptoPagoItem> | null> => {
      const response = await paymentService.listConceptosPago({
        monthlyCutId: params.filters.monthlyCutId,
        page: params.page,
        pageSize: params.pageSize,
        searchText: params.searchText ?? null,
        orderBy: params.sort?.key ?? null,
        orderDirection: params.sort?.direction ?? null,
      });

      if (!response.success || !response.data) {
        showApiError(response, translate('list.errors.loadFailed'));
        return null;
      }

      return { items: response.data.data, totalRecords: response.data.totalRecords };
    },
    [showApiError, translate],
  );

  const {
    rows,
    total: totalRecords,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading: isLoading,
    onSearch,
    onSortChange,
    setFilters,
  } = usePaginatedList<ConceptoPagoItem, ConceptosPagoFilters>({
    fetchFn,
    initialFilters: { monthlyCutId: 0 },
    initialSort: { key: 'generatedAt', direction: 'DESC' },
    enabled: hasSearched,
    orderByMap: {
      rut: 'employeeTaxIdentificationNumber',
      colaborador: 'employeeName',
      empresa: 'company',
      contrato: 'contract',
      concepto: 'concept',
      valor: 'value',
      generadoEl: 'generatedAt',
      valorAnterior: 'previousValue',
      fechaEnvio: 'sentDate',
      estado: 'status',
    },
  });

  const handleSearch = useCallback(() => {
    if (!monthlyCutId) return;
    setHasSearched(true);
    setFilters({ monthlyCutId });
  }, [monthlyCutId, setFilters]);

  const columns: TableColumn<ConceptoPagoItem>[] = useMemo(
    () => [
      { key: 'rut', header: translate('list.table.columns.rut'), sortable: true },
      { key: 'colaborador', header: translate('list.table.columns.colaborador'), sortable: true },
      { key: 'empresa', header: translate('list.table.columns.empresa'), sortable: true },
      { key: 'contrato', header: translate('list.table.columns.contrato'), sortable: true },
      { key: 'concepto', header: translate('list.table.columns.concepto'), sortable: true },
      { key: 'valor', header: translate('list.table.columns.valor'), sortable: true, align: 'right' },
      { key: 'generadoEl', header: translate('list.table.columns.generadoEl'), sortable: true },
      { key: 'valorAnterior', header: translate('list.table.columns.valorAnterior'), sortable: true, align: 'right' },
      { key: 'fechaEnvio', header: translate('list.table.columns.fechaEnvio'), sortable: true },
      { key: 'estado', header: translate('list.table.columns.estado'), sortable: true },
    ],
    [translate],
  );

  return {
    year,
    month,
    monthlyCutId,
    setYear,
    setMonth,
    setMonthlyCutId,
    yearOptions,
    monthOptions: currentMonthOptions,
    monthlyCutOptions,
    loadingMonthlyCuts,
    handleSearch,
    hasSearched,
    data: rows,
    totalRecords,
    isLoading,
    page,
    pageSize,
    setPage,
    setPageSize,
    onSearch,
    onSortChange,
    columns,
  };
};
